import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';
import ConsultationEditor from '../../doctor/ConsultationEditor';
import ClinicalButton from '../../doctor/ui/ClinicalButton';
import NurseExamWorkspace from '../workspace/NurseExamWorkspace';
import consultationService from '../../../services/consultationService';
import { useNurseWorkspace } from '../nurseWorkspaceContext';
import NursePassageHeader from '../workspace/NursePassageHeader';
import NursePassageStepper from '../workspace/NursePassageStepper';
import NurseVitalsSection from '../workspace/NurseVitalsSection';
import NurseCareTestPanel from '../workspace/NurseCareTestPanel';
import NursePassageFooter, { NURSE_PASSAGE_STEP_ORDER } from '../workspace/NursePassageFooter';
import NurseTransferModal from '../workspace/NurseTransferModal';
import NurseCloseVisitModal from '../workspace/NurseCloseVisitModal';
import {
  hasVitalsOnConsultation,
  isNursingPassageClosed,
  getNursingStatusMeta
} from '../../../constants/nursingStatus';
import '../workspace/NurseConsultationWorkspace.css';

function computeCompletedSteps(record, timeline) {
  const done = [];
  if (hasVitalsOnConsultation(record)) done.push('vitals');
  if (
    (record?.diseases?.length || 0) > 0 ||
    record?.disease_code?.trim() ||
    record?.symptoms?.trim() ||
    record?.notes?.trim()
  ) {
    done.push('exam');
  }
  if (
    (timeline?.careActions?.length || 0) > 0 ||
    (timeline?.testResults?.length || 0) > 0 ||
    (timeline?.vaccinations?.length || 0) > 0
  ) {
    done.push('acts');
  }
  return done;
}

export default function NurseConsultationEditorPage() {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { workSiteId, workSites, toast, bumpDashboardStats, paths, copy, consultationEditPath } =
    useNurseWorkspace();
  const W = copy.WORKSPACE;

  const isNewMode = consultationId === undefined || consultationId === null;

  const [record, setRecord] = useState(null);
  const [fetching, setFetching] = useState(!isNewMode);
  const [loadError, setLoadError] = useState('');
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [busyAction, setBusyAction] = useState(false);
  const [activeStep, setActiveStep] = useState('acts');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const reloadRecord = useCallback(async () => {
    if (!consultationId || isNewMode) return;
    try {
      const res = await consultationService.getConsultationById(consultationId, {
        site_id: workSiteId
      });
      if (res.success && res.data) setRecord(res.data);
    } catch {
      /* ignore */
    }
  }, [consultationId, isNewMode, workSiteId]);

  useEffect(() => {
    if (isNewMode) {
      setRecord(null);
      setLoadError('');
      setFetching(false);
      return undefined;
    }
    if (!workSiteId) {
      setFetching(true);
      setLoadError('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setFetching(true);
      setLoadError('');
      try {
        const res = await consultationService.getConsultationById(consultationId, {
          site_id: workSiteId
        });
        if (cancelled) return;
        if (res.success && res.data) {
          setRecord(res.data);
          const meta = getNursingStatusMeta(res.data.nursing_status);
          let step = meta.step || 'acts';
          if (step === 'close' || !NURSE_PASSAGE_STEP_ORDER.includes(step)) {
            step = 'acts';
          }
          setActiveStep(step);
        } else {
          setLoadError(res.message || 'Consultation introuvable.');
          setRecord(null);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(e.message || 'Chargement impossible.');
          setRecord(null);
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [consultationId, isNewMode, workSiteId]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('rx') === '1' && record?.id) {
      setActiveStep('exam');
      return;
    }
    const section = query.get('section');
    if (section && NURSE_PASSAGE_STEP_ORDER.includes(section)) {
      setActiveStep(section);
    }
  }, [location.search, record?.id]);

  const loadTimeline = useCallback(async () => {
    if (!record?.id || !workSiteId) return;
    setTimelineLoading(true);
    try {
      const res = await consultationService.getNursingTimeline(record.id, {
        site_id: workSiteId
      });
      if (res.success) setTimeline(res.data || null);
    } catch {
      setTimeline(null);
    } finally {
      setTimelineLoading(false);
    }
  }, [record?.id, workSiteId]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  const completedSteps = useMemo(
    () => computeCompletedSteps(record, timeline),
    [record, timeline]
  );

  const passageClosed = isNursingPassageClosed(record);

  const goToStep = useCallback((stepId) => {
    if (NURSE_PASSAGE_STEP_ORDER.includes(stepId)) {
      setActiveStep(stepId);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const goNext = useCallback(() => {
    const i = NURSE_PASSAGE_STEP_ORDER.indexOf(activeStep);
    if (i >= 0 && i < NURSE_PASSAGE_STEP_ORDER.length - 1) {
      goToStep(NURSE_PASSAGE_STEP_ORDER[i + 1]);
    }
  }, [activeStep, goToStep]);

  const goPrev = useCallback(() => {
    const i = NURSE_PASSAGE_STEP_ORDER.indexOf(activeStep);
    if (i > 0) goToStep(NURSE_PASSAGE_STEP_ORDER[i - 1]);
  }, [activeStep, goToStep]);

  const handleSaved = useCallback(
    (payload) => {
      bumpDashboardStats();
      toast('success', copy.TOASTS.CONSULT_SAVED);
      if (payload?.wasCreate && payload?.consultation?.id) {
        navigate(consultationEditPath(payload.consultation.id));
        return;
      }
      if (payload?.consultation) {
        setRecord(payload.consultation);
        setActiveStep('acts');
      }
    },
    [bumpDashboardStats, toast, navigate, copy, consultationEditPath]
  );

  const goList = () => navigate(paths.FILE);

  const handleTransfer = async (reason) => {
    if (!record?.id) return;
    setBusyAction(true);
    try {
      const res = await consultationService.transferNursingToMedical(record.id, { reason });
      if (res.success) {
        toast('success', 'Patient transféré vers la file médecin');
        setShowTransferModal(false);
        navigate(paths.FILE);
      }
    } catch (e) {
      toast('danger', e.message || 'Transfert impossible');
    } finally {
      setBusyAction(false);
    }
  };

  const handleClose = async (payload) => {
    if (!record?.id) return;
    setBusyAction(true);
    try {
      const res = await consultationService.closeNursingVisit(record.id, payload || {});
      if (res.success) {
        toast('success', 'Prise en charge infirmière clôturée');
        setShowCloseModal(false);
        navigate(paths.FILE);
      }
    } catch (e) {
      toast('danger', e.message || 'Clôture impossible');
    } finally {
      setBusyAction(false);
    }
  };

  if (!workSiteId) {
    return (
      <div className="nr-consult-workspace recv-dash-pro--main">
        <Alert variant="warning">
          Sélectionnez un <strong>site de travail</strong> avant de saisir une consultation.
        </Alert>
        <ClinicalButton variant="secondary" as={Link} to={paths.DASHBOARD}>
          Tableau de bord
        </ClinicalButton>
      </div>
    );
  }

  if (!isNewMode && !workSiteId) {
    return (
      <div className="text-center py-5 nr-consult-workspace nr-consult-workspace--loading">
        <Spinner animation="border" role="status" variant="success" />
        <p className="text-muted small mt-2 mb-0">Sélection du site de travail…</p>
      </div>
    );
  }

  if (!isNewMode && fetching) {
    return (
      <div className="text-center py-5 nr-consult-workspace nr-consult-workspace--loading">
        <Spinner animation="border" role="status" variant="success" />
        <p className="text-muted small mt-2 mb-0">Chargement du passage…</p>
      </div>
    );
  }

  if (!isNewMode && loadError) {
    return (
      <div className="nr-consult-workspace recv-dash-pro--main">
        <Alert variant="danger">{loadError}</Alert>
        <ClinicalButton variant="save" onClick={goList}>
          Retour à la file
        </ClinicalButton>
      </div>
    );
  }

  return (
    <div
      className={`nr-consult-workspace consult-editor-page recv-dash-pro--main${
        !isNewMode && record && activeStep === 'exam' ? ' nr-consult-workspace--exam-wide' : ''
      }${!isNewMode && record && activeStep === 'acts' ? ' nr-consult-workspace--acts-wide' : ''}`}
    >
      {!isNewMode && record ? (
        <>
          <NursePassageHeader consultation={record} onBack={goList} />

          <NursePassageStepper
            activeStep={activeStep}
            completedSteps={completedSteps}
            onStepClick={goToStep}
          />
          <p className="nr-passage-sections-hint small text-muted mb-3">{W.PASSAGE_SECTIONS_HINT}</p>

          <div
            className="nr-workspace-stage"
            role="tabpanel"
            id={`nr-panel-${activeStep}`}
            aria-labelledby={`nr-step-tab-${activeStep}`}
          >
            {activeStep === 'vitals' && (
              <NurseVitalsSection
                consultation={record}
                viewerSiteId={workSiteId}
                onUpdated={reloadRecord}
                onTimelineRefresh={loadTimeline}
              />
            )}

            {activeStep === 'acts' && (
              <NurseCareTestPanel
                consultationId={record.id}
                toast={toast}
                timeline={timeline}
                timelineLoading={timelineLoading}
                onTimelineRefresh={loadTimeline}
              />
            )}

            {activeStep === 'exam' && (
              <NurseExamWorkspace
                consultation={record}
                workSiteId={workSiteId}
                workSiteName={workSites.find((s) => String(s.id) === String(workSiteId))?.name || ''}
                onCancel={goList}
                onExamSuccess={handleSaved}
                onPrescriptionSuccess={() => {
                  bumpDashboardStats();
                  toast('success', 'Ordonnance enregistrée.');
                }}
              />
            )}
          </div>

          <NursePassageFooter
            activeStep={activeStep}
            onPrev={goPrev}
            onNext={goNext}
            passageClosed={passageClosed}
            busyAction={busyAction}
            consultation={record}
            onTransfer={() => setShowTransferModal(true)}
            onClose={() => setShowCloseModal(true)}
          />

          <NurseTransferModal
            show={showTransferModal}
            onHide={() => setShowTransferModal(false)}
            loading={busyAction}
            patientName={record.patient_name}
            onConfirm={handleTransfer}
          />
          <NurseCloseVisitModal
            show={showCloseModal}
            onHide={() => setShowCloseModal(false)}
            loading={busyAction}
            consultation={record}
            onConfirm={handleClose}
          />
        </>
      ) : (
        <>
          <header className="nr-panel mb-3">
            <h1 className="nr-panel__title mb-1">{W.NEW_TITLE}</h1>
            <p className="nr-panel__desc mb-0">{W.NEW_DESC}</p>
          </header>
          <ConsultationEditor
            consultation={null}
            viewerSiteId={workSiteId}
            clinicalWorkspace="nurse"
            showNursingVitals={false}
            onCancel={goList}
            onSuccess={handleSaved}
          />
        </>
      )}
    </div>
  );
}
